import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  InstagramLogo, FacebookLogo, YoutubeLogo, LinkedinLogo, TiktokLogo, XLogo,
  Buildings, Envelope, Phone, MapPin, WhatsappLogo,
} from "@phosphor-icons/react";

const FIELDS = [
  { key: "company_legal_name", label: "Razão social", icon: Buildings, placeholder: "NetsZap Ltda" },
  { key: "cnpj", label: "CNPJ", icon: Buildings, placeholder: "00.000.000/0001-00" },
  { key: "email", label: "E-mail de contato", icon: Envelope, placeholder: "contato@netszap.com" },
  { key: "phone", label: "Telefone", icon: Phone, placeholder: "(11) 4000-0000" },
  { key: "whatsapp", label: "WhatsApp", icon: WhatsappLogo, placeholder: "(11) 99999-9999" },
  { key: "address", label: "Endereço", icon: MapPin, placeholder: "Av. Paulista, 1000 - São Paulo/SP" },
];

const SOCIALS = [
  { key: "instagram_url", label: "Instagram", icon: InstagramLogo, placeholder: "https://instagram.com/netszap" },
  { key: "facebook_url", label: "Facebook", icon: FacebookLogo, placeholder: "https://facebook.com/netszap" },
  { key: "twitter_url", label: "Twitter / X", icon: XLogo, placeholder: "https://x.com/netszap" },
  { key: "youtube_url", label: "YouTube", icon: YoutubeLogo, placeholder: "https://youtube.com/@netszap" },
  { key: "linkedin_url", label: "LinkedIn", icon: LinkedinLogo, placeholder: "https://linkedin.com/company/netszap" },
  { key: "tiktok_url", label: "TikTok", icon: TiktokLogo, placeholder: "https://tiktok.com/@netszap" },
];

export default function MasterSiteSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/master/site-settings")
      .then((r) => setSettings(r.data || {}))
      .finally(() => setLoading(false));
  }, []);

  const setField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/master/site-settings", settings);
      setSettings(data);
      toast.success("Configurações salvas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-sm text-[#5C5C59]">Carregando…</div>;

  return (
    <div className="p-8 max-w-5xl" data-testid="master-site-settings">
      <div className="mb-8">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Configurações do site</p>
        <h1 className="text-4xl font-black tracking-tighter mt-1">Contato, redes e trial</h1>
        <p className="text-sm text-[#5C5C59] mt-2">
          Essas informações aparecem na landing page pública (rodapé e signup).
        </p>
      </div>

      {/* Contact */}
      <section className="bg-white border border-[#E5E5E0] shadow-brutal p-6 mb-6">
        <h2 className="text-lg font-black tracking-tight mb-4">Contato & Empresa</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <Label className="text-xs uppercase font-semibold tracking-wider flex items-center gap-1.5">
                <f.icon size={14} weight="bold" /> {f.label}
              </Label>
              <Input
                data-testid={`settings-input-${f.key}`}
                value={settings[f.key] || ""}
                onChange={(e) => setField(f.key, e.target.value)}
                placeholder={f.placeholder}
                className="rounded-none mt-1"
              />
            </div>
          ))}
        </div>
        <div className="mt-4">
          <Label className="text-xs uppercase font-semibold tracking-wider">Frase do rodapé</Label>
          <Textarea
            data-testid="settings-input-footer_tagline"
            value={settings.footer_tagline || ""}
            onChange={(e) => setField("footer_tagline", e.target.value)}
            className="rounded-none mt-1 font-serif"
            rows={2}
          />
        </div>
      </section>

      {/* Socials */}
      <section className="bg-white border border-[#E5E5E0] shadow-brutal p-6 mb-6">
        <h2 className="text-lg font-black tracking-tight mb-4">Redes sociais (URLs completas)</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {SOCIALS.map((f) => (
            <div key={f.key}>
              <Label className="text-xs uppercase font-semibold tracking-wider flex items-center gap-1.5">
                <f.icon size={14} weight="fill" /> {f.label}
              </Label>
              <Input
                data-testid={`settings-input-${f.key}`}
                value={settings[f.key] || ""}
                onChange={(e) => setField(f.key, e.target.value)}
                placeholder={f.placeholder}
                className="rounded-none mt-1 font-mono text-xs"
              />
            </div>
          ))}
        </div>
      </section>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="settings-save-btn"
          className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white px-8 py-6 text-base"
        >
          {saving ? "Salvando…" : "Salvar configurações"}
        </Button>
      </div>
    </div>
  );
}
